import { produce } from 'immer';
import { useEffect, useMemo } from 'react';

import { manifestModelAndProvider } from '@/Pages/Playground/components/manifest/serialized/ManifestConstructor';
import { isAnyOSeriesModel } from '@/Pages/Playground/components/manifest/serialized/chat_models/ManifestChatOpenAI.utils';
import { convertManifestToMessages } from '@/Pages/Playground/components/manifest/serialized/prompts/ManifestChatPromptTemplate.utils';
import { getNonRunnableBindingModelFromManifest } from '@/Pages/Playground/utils/Playground.utils';
import { EMessageType } from '@/components/EditableMessage/types';
import { convertSerializedMessagesToMessageTuple } from '@/components/Rules/RulesEvaluatorPane.utils';

import { useEvaluatorStore } from '../store/evaluatorStore';
import { processFewShotExamples } from './fewShotExamplesUtils';
import { schemaHasOneFeedbackProperty } from './schemaHasOneFeedbackProperty';
import { EvaluatedEntityType } from './serializeFeedbackUIToLC';
import { useHubTypeManifest } from './useHubTypeManifest';
import { usePromptTypeManifest } from './usePromptTypeManifest';

// Helper function to detect if a manifest contains multimodal content
export const hasMultimodalContent = (manifest: any): boolean => {
  if (!manifest?.kwargs?.first?.kwargs?.messages) {
    return false;
  }

  try {
    const messages = convertManifestToMessages(
      manifest.kwargs.first.kwargs.messages
    );

    return messages.some((message) => {
      // Check if message is a StoredMessage with data property
      if (
        'data' in message &&
        message.data?.content &&
        Array.isArray(message.data.content)
      ) {
        return message.data.content.some((contentPart: any) => {
          return contentPart.type && contentPart.type !== 'text';
        });
      }
      return false;
    });
  } catch (error) {
    console.error('Error checking for multimodal content:', error);
    return false;
  }
};

export function useParsedManifest(evaluatedEntityType: EvaluatedEntityType) {
  const evaluatorType = useEvaluatorStore((state) => state.evaluatorType);
  const promptManifest = usePromptTypeManifest(evaluatedEntityType);
  const evaluatorVersion = useEvaluatorStore((state) => state.evaluatorVersion);
  const { manifest: hubManifest, isLoading: isHubManifestLoading } =
    useHubTypeManifest();
  const setHubRefManifest = useEvaluatorStore(
    (state) => state.setHubRefManifest
  );
  const storedHubManifest = useEvaluatorStore((state) => state.hubRefManifest);
  const setModel = useEvaluatorStore((state) => state.setModel);
  const currentModel = useEvaluatorStore((state) => state.model);
  const setFewShotContent = useEvaluatorStore(
    (state) => state.setFewShotContent
  );
  const setFeedbackEditorType = useEvaluatorStore(
    (state) => state.setFeedbackEditorType
  );
  const setFewShotExamplesMap = useEvaluatorStore(
    (state) => state.setFewShotExamplesMap
  );
  const variableMapping = useEvaluatorStore((state) => state.variableMapping);
  const setSchema = useEvaluatorStore((state) => state.setFeedbackSchema);

  // Parse few shot examples outside of useEffect - happens every time hubManifest changes
  const { fewShotContent, updatedManifest } = useMemo(() => {
    if (!hubManifest || isHubManifestLoading) {
      return { updatedManifest: null, fewShotContent: '' };
    }

    const promptMessages = convertSerializedMessagesToMessageTuple(
      hubManifest.kwargs.first.kwargs.messages
    );

    const { processedMessages, fewShotContent } =
      processFewShotExamples(promptMessages);

    const updatedManifest = produce(hubManifest, (draft) => {
      draft.kwargs.first.kwargs.messages = processedMessages.map(
        ([_, messageContent], index) => {
          const msg = draft.kwargs.first.kwargs.messages[index];
          if (msg?.kwargs?.prompt?.kwargs?.template) {
            msg.kwargs.prompt.kwargs.template = messageContent;
          }
          return msg;
        }
      );
      draft.kwargs.last = currentModel ? currentModel : draft.kwargs.last;
    });

    return { fewShotContent, updatedManifest };
  }, [hubManifest, isHubManifestLoading, currentModel]);

  useEffect(() => {
    if (evaluatorType === 'hub' && updatedManifest && !isHubManifestLoading) {
      // Set the manifest and few-shot state
      const shouldEnableFewShot = Boolean(fewShotContent);
      const fewShotExamplesMap = shouldEnableFewShot
        ? variableMapping || {}
        : {};

      setHubRefManifest(updatedManifest);
      setFewShotContent(fewShotContent);
      setFewShotExamplesMap(fewShotExamplesMap);
      setSchema(updatedManifest.kwargs.first.kwargs.schema_);
      if (
        schemaHasOneFeedbackProperty(
          updatedManifest.kwargs.first.kwargs.schema_
        )
      ) {
        setFeedbackEditorType('pretty');
      }
      // Only set the model if it's not already set (initial load from hub)
      const modelFromManifest =
        getNonRunnableBindingModelFromManifest(updatedManifest);
      if (!currentModel && evaluatorVersion >= 3 && modelFromManifest != null) {
        setModel(modelFromManifest);
      }
    }
  }, [
    hubManifest,
    setHubRefManifest,
    setModel,
    currentModel,
    isHubManifestLoading,
    setFewShotExamplesMap,
    evaluatorType,
    updatedManifest,
    fewShotContent,
    setFewShotContent,
    setSchema,
    evaluatorVersion,
    setFeedbackEditorType,
  ]);
  // variableMapping removed as a prop because it changes when the user edits the prompt
  // but this editing is valid and I don't want to reset the evaluatorStore in that case

  const manifest =
    evaluatorType === 'prompt'
      ? promptManifest
      : evaluatorVersion == 3 && storedHubManifest.kwargs?.last
      ? storedHubManifest
      : updatedManifest ?? hubManifest;
  const manifestLast =
    getNonRunnableBindingModelFromManifest(manifest) ?? manifest.kwargs.last;

  const modelAndProvider = manifestModelAndProvider({
    variant: 'line',
    value: manifestLast,
    options: {},
  });
  const { isO1Generic } = isAnyOSeriesModel(modelAndProvider?.model ?? '');

  const manifestStructuredSchema =
    manifest.kwargs.first.kwargs.schema || manifest.kwargs.first.kwargs.schema_;
  const disabledMessageTypes = useMemo(
    () => getDisabledMessageTypes(modelAndProvider?.model ?? ''),
    [modelAndProvider?.model]
  );

  return {
    manifest,
    manifestLast,
    modelAndProvider,
    isO1Generic,
    manifestStructuredSchema,
    disabledMessageTypes,
    isHubManifestLoading,
  };
}

export const getDisabledMessageTypes = (model: string) => {
  const { isO1Generic } = isAnyOSeriesModel(model);
  const types = [
    EMessageType.FUNCTION,
    EMessageType.TOOL,
    EMessageType.CHAT,
    EMessageType.PLACEHOLDER,
  ];
  if (Object.values(isO1Generic).some((x) => x)) {
    types.push(EMessageType.SYSTEM);
  }
  return types;
};
